/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AuthContext } from '../context';
import { BackIcon, MenuIcon, UserMenuIcon } from '../assets/icons';
import { Logo } from '../assets/img';
import { classNames, Constants, Socket, SocketEvents } from '../utils';
import { MENU_ADMIN, MENU_MODERATOR, MENU_OWNER } from './menu';

const Layout = ({ children }) => {
  const confirmModalRef = useRef();
  const userMenuRef = useRef();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [navigation, setNavigation] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const getUserMenu = () => {
    // No está loggeado
    if (!user || ![Constants.LEVELS.ADMIN, Constants.LEVELS.MODERATOR, Constants.LEVELS.OWNER].includes(user.level_id))
      return;

    const breadcrumbs = [];

    const searchSubmodules = (item) => {
      if (item?.href) breadcrumbs.push({ name: item?.breadcumb || item.name, href: item.href });

      if (item?.modules?.length) {
        item.modules.forEach(subItem => searchSubmodules(subItem));
      }
      if (item?.nested?.length) {
        item.nested.forEach(subItem => searchSubmodules(subItem));
      }
    }

    let filteredMenu = [];

    // Es un moderador
    if (user.level_id === Constants.LEVELS.MODERATOR) {
      filteredMenu = MENU_MODERATOR.filter(item => {
        const isForAllUsers = !item.profile;
        const hasPermission = item.profile?.includes(user.moderator_type_id);
        return isForAllUsers || hasPermission;
      });

    } else if (user.level_id === Constants.LEVELS.OWNER) {
      filteredMenu = MENU_OWNER;

    } else {
      filteredMenu = MENU_ADMIN;
    }

    // Obtetiene los enlaces del breadcrumb en base al menú asignado
    filteredMenu.forEach(item => searchSubmodules(item));
    setNavigation(filteredMenu);
    setBreadcrumbs(breadcrumbs);
  }

  const routeName = breadcrumbs.find(x => {
    const currentParts = location.pathname.split('/');
    const pathParts = x?.href?.split('/') || [];

    if (currentParts.length !== pathParts.length) return false;

    return pathParts.every((item, i) => {
      const _current = currentParts[i];
      return pathParts[i].includes(':') ? true : item === _current;
    });
  })?.name || '';

  const toggleSidebar = () => {
    const items = document.getElementsByClassName('desk-sidebar');
    for (let key = 0; key < items.length; key++) {
      items.item(key).classList.toggle('open');
    }
  }

  const userProfile = useMemo(() => {
    switch (user?.level_id) {
      case Constants.LEVELS.MODERATOR:
        return user?.moderator_type_id === Constants.MODERATOR_TYPE.COORDINATOR
          ? 'Coordinador'
          : 'Supervisor';

      case Constants.MODERATOR_TYPE.COORDINATOR:
        return 'Propietario';

      default:
        return 'Administrador'
    }
  }, [user]);

  useEffect(() => {
    getUserMenu();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    Socket.on(SocketEvents.USER.DISABLE_USER, (data) => {
      if (data.user_id === user?.id) {
        toast('Cuenta inactiva. Tu usuario ha sido desactivado temporalmente.', { icon: '⚠' })
        navigate('/logout', { replace: true });
      }
    });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      Socket.off(SocketEvents.USER.DISABLE_USER);
    };
  }, []);

  return (
    <div id="layout">
      <dialog ref={confirmModalRef} className="fixed inset-0 z-50 overflow-y-auto bg-transparent">
        <div className="w-[20rem] bg-white rounded-xl overflow-hidden">
          <div className="py-8 px-5">
            <p className="text-center">¿Realmente desea cerrar la sesión?</p>
          </div>

          <div className="flex justify-between h-12 font-semibold">
            <button
              className="flex-1 bg-white p-3"
              onClick={() => confirmModalRef.current?.close()}
            >
              <span>Cancelar</span>
            </button>

            <button className="flex-1" onClick={() => navigate('/logout', { replace: true })}>
              <span>Aceptar</span>
            </button>
          </div>
        </div>
      </dialog>

      <div className="desk-sidebar desk-sidebar__header">
        <div className="sidebar-logo">
          <div className="sidebar-logo__wrapper">
            {/* <button type="button" className="w-8 h-8 p-1.5 ml-2 flex justify-center items-center rounded-lg" onClick={toggleSidebar}>
              <span className="sr-only">Open sidebar</span>
              <img src={MenuIcon} className="w-4 h-4 object-contain" alt="" />
            </button> */}

            <img src={Logo} className="object-contain" alt="" />
          </div>
        </div>
        <div className="desk-headerbar">
          <button type="button" className="w-8 h-8 flex justify-center items-center rounded-lg" onClick={toggleSidebar}>
            <span className="sr-only">Open sidebar</span>
            <img src={MenuIcon} className="w-8 h-8 object-contain" alt="" />
          </button>

          <div id="header-goback" className="ml-4 hidden" onClick={() => navigate?.goBack()}>
            <img src={BackIcon} className="w-6 h-6 object-contain cursor-pointer" title="Volver" alt="Volver" />
          </div>

          <div className="h-6 w-px bg-[#F5F7FA80]" aria-hidden="true" />

          <span id="breadcrumb-content" className="hidden md:block font-bold text-2xl text-black">
            { routeName }
          </span>

          <div className="flex flex-1 gap-x-4 self-stretch items-center lg:gap-x-6">
            <div className="relative flex flex-1" />

            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-x-2 focus:outline-none">
                  <span className="font-semibold text-text mr-2">
                    { user?.person?.name || userProfile }
                  </span>
                  <img className="w-8 h-8 object-contain" src={UserMenuIcon} alt="" />
                  <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="red" className={classNames('transition-all', isUserMenuOpen ? 'rotate-0' : 'rotate-90')}>
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M18.707 8.293a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1 -1.414 0l-6 -6a1 1 0 0 1 1.414 -1.414l5.293 5.293l5.293 -5.293a1 1 0 0 1 1.414 0" />
                  </svg>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                    <button
                      className="block w-full px-4 py-2 text-left font-sans text-sm text-text hover:bg-gray5"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        confirmModalRef.current?.showModal();
                      }}
                    >
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <SideBar navigation={navigation} />

      <main>
        {!!user && <Outlet />}
      </main>
    </div>
  )
}

const SideBar = ({ navigation }) => {
  return (
    <div className="desk-sidebar">
      <aside>
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-[#242424] bg-[#242424] px-6 pb-4 transition-all duration-100">
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col -mx-2 space-y-3">
              {navigation.filter(x => !x.hidden).map((item, index) => (
                <MenuItem key={index} item={item} />
              ))}
            </ul>
          </nav>
        </div>
      </aside>
    </div>
  )
}

export const MenuItem = ({ item }) => {
  const location = useLocation();
  const [showItems, setShowItems] = useState(false);

  const isActive = useMemo(() => {
    if (!item?.href) {
      const innerPaths = [];
      item?.nested?.map(x => innerPaths.push(x.href)) ;
      item?.modules?.map(x => innerPaths.push(x.href));

      return innerPaths.some(x => {
        if (String(item.href).includes(':')) {
          const currentParts = location.pathname.split('/');
          const pathParts = item?.href?.split('/') || [];

          if (currentParts.length !== pathParts.length) return false;

          return pathParts.every((item, i) => {
            const _current = currentParts[i];
            return pathParts[i].includes(':') ? true : item === _current;
          });

        } else {
          return location.pathname === x;
        }
      })
    }

    return location.pathname === item?.href;
  });

  const openSidebar = () => {
    const items = document.getElementsByClassName('desk-sidebar');
    if (items.item(0).classList.contains('open')) return;

    for (let key = 0; key < items.length; key++) {
      if (!items.item(key).classList.contains('open')) {
        items.item(key).classList.toggle('open');
      }
    }
    setShowItems(true);
  }

  if (!item?.modules?.length) return (
    <li className="menu-item" title={item.name} data-level="root" data-active={String(isActive)}>
      <Link
        to={item.href}
        className="text-white group flex gap-x-3 rounded-md p-2 leading-6 font-semibold"
      >
        <img
          src={isActive ? item.iconActive : item.icon}
          className="w-5 object-contain shrink-0"
          alt=""
        />
        <span>{ item.name }</span>
      </Link>
    </li>
  )

  return (
    <li className="menu-item" title={item.name} onClick={openSidebar} data-level="root" data-active={String(isActive)}>
      <Link
        to={item.modules[0].href}
        className="text-white group flex gap-x-3 rounded-md p-2 leading-6 font-semibold"
        onClick={() => setShowItems(!showItems)}
      >
        {!!item?.icon && (
          <img
            className={classNames('w-5 object-contain shrink-0', isActive ? 'text-primary':'text-gray-500 group-hover:text-primary')}
            src={isActive ? item.iconActive : item.icon}
            alt=""
          />
        )}
        <span className="item-label flex-1">{ item.name }</span>
        <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="#888888" className={classNames('transition-all duration-100', showItems ? 'rotate-90':'rotate-0')}>
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M18.707 8.293a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1 -1.414 0l-6 -6a1 1 0 0 1 1.414 -1.414l5.293 5.293l5.293 -5.293a1 1 0 0 1 1.414 0" />
        </svg>
      </Link>

      {showItems && (
        <ul className="pl-9 space-y-3">
          {item?.modules?.map((subItem, subIdx) => {
            let isSubActive = false;

            if (String(subItem.href).includes(':')) {
              const currentParts = location.pathname.split('/');
              const pathParts = subItem?.href?.split('/') || [];

              if (currentParts.length !== pathParts.length) return false;

              return pathParts.every((part, i) => {
                const _current = currentParts[i];
                return pathParts[i].includes(':') ? true : part === _current;
              });

            } else {
              isSubActive = location.pathname === subItem?.href;
            }

            if (!subItem?.modules?.length) return (
              <li key={subIdx} className="menu-item" title={subItem.name} data-level="submenu" data-active={String(isSubActive)}>
                <Link
                  to={subItem?.href}
                  className="text-white group flex gap-x-3 rounded-md p-2 leading-6 font-semibold"
                >
                  {!!subItem?.icon && (
                    <img src={subItem.icon} className="w-5 object-contain shrink-0" alt="" />
                  )}
                  <span className="subitem-label">{ subItem.name }</span>
                </Link>
              </li>
            )

            return (
              <MenuItem key={'sub' + subIdx} item={subItem} />
            )
          })}
        </ul>
      )}
    </li>
  )
}

export default Layout;
